/**
 * CodeMirror completion source for leedoc markdown extensions.
 *
 * Provides three kinds of completions in the source editor:
 * 1. Component names while typing inside `<!-- embed: ... -->`
 * 2. Page paths while typing inside `[[wikilinks]]`
 * 3. Leedoc marker snippets (embed, tangle, vars, toc, collapse, ...) —
 *    triggered by starting an HTML comment (`<`), typing `[ld`, typing `/`
 *    on an empty line (WordPress-style block inserter), or Ctrl+Space.
 */

import {
  snippetCompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
  type CompletionSource,
} from '@codemirror/autocomplete';

export interface LeedocCompletionOptions {
  /** Component names as used in embed directives (e.g. "docker-install.md") */
  getComponentNames: () => string[];
  /** All project file paths (used for wikilink targets; .src.md filtered here) */
  getPagePaths: () => string[];
}

/** Snippets for leedoc's markdown extensions. Labels are the literal marker
 *  prefixes so they match what the user has already typed (`<!-- e`, `[ld`). */
const MARKER_SNIPPETS: Completion[] = [
  snippetCompletion('<!-- embed: ${component.md} -->', {
    label: '<!-- embed: -->',
    detail: 'Embed component (docs only)',
    type: 'keyword',
    boost: 2,
  }),
  snippetCompletion('<!-- embed: ${component.md} | ${target.sh} -->', {
    label: '<!-- embed: | target -->',
    detail: 'Embed component with script target',
    type: 'keyword',
    boost: 1,
  }),
  snippetCompletion('<!-- tangle: ${script.sh} -->\n```bash\n${}\n```', {
    label: '<!-- tangle: -->',
    detail: 'Script extraction marker + code block',
    type: 'keyword',
  }),
  snippetCompletion('<!-- vars\n@${VAR}=${value}\n-->', {
    label: '<!-- vars -->',
    detail: 'Variable definitions block',
    type: 'keyword',
  }),
  snippetCompletion('<!-- collapse: ${Title} | ${15} -->', {
    label: '<!-- collapse -->',
    detail: 'Collapsible code block marker',
    type: 'keyword',
  }),
  snippetCompletion('[ld:toc]', {
    label: '[ld:toc]',
    detail: 'Table of contents',
    type: 'keyword',
  }),
  snippetCompletion('[ld:toc:${2-3}]', {
    label: '[ld:toc:N]',
    detail: 'Table of contents with depth',
    type: 'keyword',
  }),
];

/** Same actions with slash-command labels for the `/` block-inserter menu */
const SLASH_COMMANDS: Completion[] = [
  snippetCompletion('<!-- embed: ${component.md} -->', {
    label: '/embed',
    detail: 'Embed component',
    type: 'keyword',
    boost: 2,
  }),
  snippetCompletion('<!-- embed: ${component.md} | ${target.sh} -->', {
    label: '/embed-target',
    detail: 'Embed component with script target',
    type: 'keyword',
  }),
  snippetCompletion('<!-- tangle: ${script.sh} -->\n```bash\n${}\n```', {
    label: '/tangle',
    detail: 'Script extraction marker + code block',
    type: 'keyword',
  }),
  snippetCompletion('<!-- vars\n@${VAR}=${value}\n-->', {
    label: '/vars',
    detail: 'Variable definitions block',
    type: 'keyword',
  }),
  snippetCompletion('[ld:toc]', {
    label: '/toc',
    detail: 'Table of contents',
    type: 'keyword',
  }),
  snippetCompletion('<!-- collapse: ${Title} | ${15} -->', {
    label: '/collapse',
    detail: 'Collapsible code block marker',
    type: 'keyword',
  }),
  snippetCompletion('## 0. ${Title}', {
    label: '/heading',
    detail: 'Numbered heading',
    type: 'keyword',
  }),
  snippetCompletion('[[${page}]]', {
    label: '/link',
    detail: 'Wikilink to another page',
    type: 'keyword',
  }),
  snippetCompletion('![${alt}](media://${file.png})', {
    label: '/image',
    detail: 'Media library image',
    type: 'keyword',
  }),
];

export function leedocCompletionSource(opts: LeedocCompletionOptions): CompletionSource {
  return (context: CompletionContext): CompletionResult | null => {
    const line = context.state.doc.lineAt(context.pos);
    const textBefore = context.state.sliceDoc(line.from, context.pos);
    const textAfter = context.state.sliceDoc(context.pos, line.to);

    // 1) Component names inside an embed directive
    const embed = /<!--\s*embed:\s*([^|>]*)$/.exec(textBefore);
    if (embed) {
      const partial = embed[1].replace(/^\s+/, '');
      const from = context.pos - partial.length;
      // If the directive isn't closed yet, complete the closing too
      const needsClose = !/^\s*(\||-->)/.test(textAfter);
      const options: Completion[] = opts.getComponentNames().map((name) => ({
        label: name,
        type: 'class',
        apply: needsClose ? `${name} -->` : name,
      }));
      if (options.length === 0) return null;
      return { from, options, validFor: /^[^|>\n]*$/ };
    }

    // 2) Page paths inside a wikilink
    const wiki = /\[\[([^\][|#]*)$/.exec(textBefore);
    if (wiki) {
      const partial = wiki[1];
      const from = context.pos - partial.length;
      const options: Completion[] = opts
        .getPagePaths()
        .filter((p) => p.endsWith('.src.md') && !p.startsWith('_components/'))
        .map((p) => ({
          label: p.replace(/\.src\.md$/, ''),
          type: 'text',
        }));
      if (options.length === 0) return null;
      return { from, options, validFor: /^[^\][|#\n]*$/ };
    }

    // 3) Slash block-inserter on an otherwise-empty line
    const slash = /^\s*(\/[\w-]*)$/.exec(textBefore);
    if (slash) {
      return {
        from: context.pos - slash[1].length,
        options: SLASH_COMMANDS,
        validFor: /^\/[\w-]*$/,
      };
    }

    // 4) Marker snippets when starting an HTML comment or `[ld`, or explicitly
    const marker = /(?:<!?-?-?\s*[\w-]*|\[ld[\w:.-]*)$/.exec(textBefore);
    if (marker) {
      return {
        from: context.pos - marker[0].length,
        options: MARKER_SNIPPETS,
      };
    }
    if (context.explicit) {
      return { from: context.pos, options: MARKER_SNIPPETS };
    }

    return null;
  };
}
